import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Loader2, Lock, Trash2, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CARD_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#0F172A",
      "::placeholder": { color: "#94A3B8" },
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
    invalid: { color: "#dc2626" },
  },
};

function AddCardInner({ onSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const { data: si } = await api.post("/payments/setup-intent");
      const result = await stripe.confirmCardSetup(si.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        toast.error(result.error.message || "Could not save card");
      } else if (result.setupIntent?.status === "succeeded") {
        toast.success("Card saved");
        elements.getElement(CardElement).clear();
        onSaved?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save card");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="add-card-form">
      <div className="border border-slate-200 rounded-lg px-3.5 py-3.5 bg-white focus-within:border-brand-blue focus-within:ring-2 focus-within:ring-brand-blue/20 transition">
        <CardElement options={CARD_OPTIONS} />
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Lock className="h-3 w-3" />
        Card info is encrypted by Stripe and never touches our servers.
      </div>
      <Button
        type="submit"
        disabled={!stripe || busy}
        className="w-full gap-2"
        data-testid="add-card-submit"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
        {busy ? "Saving…" : "Save card"}
      </Button>
    </form>
  );
}

function CardList({ methods, defaultId, onChanged }) {
  const remove = async (id) => {
    if (!window.confirm("Remove this card?")) return;
    try {
      await api.delete(`/payments/methods/${id}`);
      toast.success("Card removed");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove card");
    }
  };

  if (!methods.length) return null;
  return (
    <ul className="space-y-2 mb-4" data-testid="card-list">
      {methods.map((m) => (
        <li
          key={m.id}
          className="flex items-center justify-between border border-slate-200 rounded-lg px-3.5 py-2.5 bg-slate-50"
          data-testid={`card-row-${m.id}`}
        >
          <div className="flex items-center gap-3">
            <CreditCard className="h-4 w-4 text-slate-500" />
            <div>
              <div className="font-semibold text-slate-800 text-sm capitalize">
                {m.brand} •••• {m.last4}
                {defaultId === m.id && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">
                    Default
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">Expires {String(m.exp_month).padStart(2, "0")}/{String(m.exp_year).slice(-2)}</div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => remove(m.id)}
            data-testid={`card-remove-${m.id}`}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
          >
            <Trash2 className="h-4 w-4" /> Remove
          </Button>
        </li>
      ))}
    </ul>
  );
}

function HostedCheckoutButton({ size = "default", testId = "stripe-hosted-checkout" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/setup-checkout");
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        toast.error("Couldn't open Stripe Checkout");
        setBusy(false);
      }
    } catch (err) {
      console.error("[HostedCheckoutButton] failed:", err);
      toast.error(err?.response?.data?.detail || "Couldn't open Stripe Checkout");
      setBusy(false);
    }
  };
  return (
    <Button type="button" onClick={go} size={size} disabled={busy} data-testid={testId} className="gap-2">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
      Add card on a secure Stripe page
    </Button>
  );
}

export default function StripeCardManager({ onChanged }) {
  const [pk, setPk] = useState(null);
  const [methods, setMethods] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeJsState, setStripeJsState] = useState("idle"); // idle | loading | ready | failed

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: pkData }, { data: mData }] = await Promise.all([
        api.get("/payments/publishable-key"),
        api.get("/payments/methods"),
      ]);
      if (!pkData?.publishable_key) {
        throw new Error("Stripe publishable key missing from server");
      }
      setPk(pkData.publishable_key);
      setMethods(mData.payment_methods || []);
      setDefaultId(mData.default || null);
    } catch (err) {
      console.error("[StripeCardManager] load failed:", err);
      const msg =
        err?.response?.status === 401
          ? "Please sign in again to manage your card."
          : err?.message === "Network Error" || err?.code === "ERR_NETWORK"
          ? "Couldn't reach the server. Check your connection and try again."
          : err?.response?.data?.detail || err?.message || "Couldn't load payment options.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Probe Stripe.js: detect blocked CDN within 8s and surface a clear fallback path.
  // `loadStripe()` always returns a Promise — it resolves to `null` (not throws)
  // when js.stripe.com is unreachable, so we await it ourselves.
  const stripePromise = useMemo(() => (pk ? loadStripe(pk) : null), [pk]);
  useEffect(() => {
    if (!stripePromise) {
      setStripeJsState("idle");
      return;
    }
    let done = false;
    setStripeJsState("loading");
    const timeout = setTimeout(() => {
      if (!done) {
        done = true;
        setStripeJsState("failed");
        console.error("[StripeCardManager] Stripe.js load timed out (>8s)");
      }
    }, 8000);
    stripePromise
      .then((s) => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        setStripeJsState(s ? "ready" : "failed");
        if (!s) console.error("[StripeCardManager] Stripe.js returned null (blocked by browser/network)");
      })
      .catch((e) => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        setStripeJsState("failed");
        console.error("[StripeCardManager] Stripe.js threw:", e);
      });
    return () => {
      done = true;
      clearTimeout(timeout);
    };
  }, [stripePromise]);

  const handleSaved = () => {
    load();
    onChanged?.();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="cards-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading cards…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="cards-error" className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-sm font-semibold text-red-900 mb-1">Card form couldn't load</div>
        <div className="text-xs text-red-800 mb-3">{error}</div>
        <div className="flex flex-col sm:flex-row gap-2 items-start">
          <Button type="button" onClick={load} size="sm" data-testid="cards-retry" className="gap-2">
            <Loader2 className="h-3.5 w-3.5" /> Try again
          </Button>
          <HostedCheckoutButton size="sm" testId="cards-error-hosted-checkout" />
          <a
            href="tel:480-940-6023"
            className="text-xs font-semibold text-red-900 underline self-center sm:self-auto sm:py-1.5"
            data-testid="cards-call-fallback"
          >
            Or call us at 480-940-6023
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="stripe-card-manager">
      <CardList methods={methods} defaultId={defaultId} onChanged={handleSaved} />

      {stripeJsState === "ready" && stripePromise && (
        <Elements stripe={stripePromise}>
          <AddCardInner onSaved={handleSaved} />
        </Elements>
      )}

      {stripeJsState === "loading" && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="stripe-js-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Preparing secure card field…
        </div>
      )}

      {stripeJsState === "failed" && (
        <div data-testid="stripe-blocked" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-semibold text-amber-900 mb-1">In-page card field is blocked</div>
          <div className="text-xs text-amber-800 mb-3">
            Your browser couldn't load the secure card field (often due to an ad-blocker, strict privacy mode, VPN, or corporate network blocking <code>js.stripe.com</code>). You can still save a card on Stripe's official hosted page — same security, no installation needed.
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <HostedCheckoutButton size="default" testId="stripe-blocked-hosted-checkout" />
            <Button type="button" variant="outline" onClick={load} size="default" data-testid="stripe-blocked-retry" className="gap-2">
              <Loader2 className="h-4 w-4" /> Try in-page form again
            </Button>
          </div>
        </div>
      )}

      {stripeJsState === "ready" && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => setStripeJsState("failed")}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="prefer-hosted-checkout"
          >
            Card form not working? Use Stripe's secure hosted page instead
          </button>
        </div>
      )}
    </div>
  );
}
